import React, { useEffect, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { Menu as MenuIcon, ArrowLeft, PanelRight, Loader2 } from 'lucide-react';
import { useHistory, useLocation } from '../../hooks/useRouterCompat';
import { useAppUIContext } from '../../context/AppUIContext';
import { useResponsive } from '../../hooks/useResponsive';
import { MyNotifs } from '../../services/MyNotifs';
import { My } from '../../services/My';
import { cn } from '../../lib/utils';

import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '../../components/ui/sheet';
import { Dialog, DialogContent } from '../../components/ui/dialog';

import { AiMemoView } from '../../models/AiMemo';
import { AiMemoSidebarContent } from './AiMemoSidebarContent';
import AiMemoList from './AiMemoList';
import AiMemoCreate from './AiMemoCreate';
import AiMemoDetail from './AiMemoDetail';
import AiMinutesList from './AiMinutesList';
import AiMinutesCreate from './AiMinutesCreate';
import AiMinutesDetail from './AiMinutesDetail';

type AiMemoSubView =
    | 'memos'
    | 'minutes';

const AiMemoPage: React.FC = () => {
    const { setIsMobileMenuOpen } = useAppUIContext();
    const { isMobileOrTablet } = useResponsive();
    const history = useHistory();
    const location = useLocation();
    const [sheetOpen, setSheetOpen] = useState(false);
    const [counter, setCounter] = useState(0);

    // 모달 상태
    const [showMemoCreate, setShowMemoCreate] = useState(false);
    const [showMinutesCreate, setShowMinutesCreate] = useState(false);
    const [detailMemoId, setDetailMemoId] = useState<string | null>(null);
    const [detailMinutesId, setDetailMinutesId] = useState<string | null>(null);

    // Parse view from URL query
    const query = new URLSearchParams(location.search);
    const viewParam = query.get('view') || 'memos';
    const itemId = query.get('id');
    const roomIdParam = query.get('roomId') || My.user?.id || '';
    const [currentSubView, setCurrentSubView] = useState<AiMemoSubView>(viewParam as AiMemoSubView);

    useEffect(() => {
        let unsubNotifs = MyNotifs.subscribeToNotificationUpdates((_) => {
            setCounter(MyNotifs.NrOfNew);
        });
        return () => { unsubNotifs(); }
    }, []);

    // Sync with URL changes
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const view = params.get('view') || 'memos';
        setCurrentSubView(view as AiMemoSubView);
    }, [location.search]);

    const navigateTo = (view: AiMemoSubView, id?: string) => {
        let url = `/ai-memo?view=${view}`;
        if (id) url += `&id=${id}`;
        
        const queryParams = new URLSearchParams(location.search);
        const currentRoomId = queryParams.get('roomId');
        if (currentRoomId) url += `&roomId=${currentRoomId}`;
        
        history.push(url);
    };

    // Map subview to sidebar view for highlighting
    const getSidebarView = (): AiMemoView => {
        if (currentSubView.startsWith('memos')) return 'memos';
        if (currentSubView.startsWith('minutes')) return 'minutes';
        return currentSubView as AiMemoView;
    };

    const handleSidebarViewChange = (view: AiMemoView) => {
        navigateTo(view as AiMemoSubView);
    };

    const getTitle = (): string => {
        switch (currentSubView) {
            case 'memos': return t`Memos`;
            case 'minutes': return t`Meeting Minutes`;
            default: return t`AI Memo`;
        }
    };

    const canGoBack = currentSubView.includes('-');

    return (
        <div className="flex flex-col h-full overflow-hidden bg-background md:rounded-panel md:border md:border-border">
            {/* Header */}
            <header className="z-40 bg-background border-b min-w-0 flex items-center justify-between px-4 py-4 shrink-0 shadow-sm relative overflow-hidden">
                <div className="flex items-center gap-2 w-full">
                    <div className="shrink-0 lg:hidden">
                        <Button
                            variant="ghost"
                            size="icon"
                            data-cy="menu"
                            onClick={() => setIsMobileMenuOpen(true)}
                            className="relative"
                        >
                            <MenuIcon className="h-5 w-5" />
                            {!!counter && (
                                <Badge variant="destructive" className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 rounded-full text-meta-sm">
                                    {counter}
                                </Badge>
                            )}
                        </Button>
                    </div>

                    <div className="flex flex-1 items-center justify-between min-w-0">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            {canGoBack && (
                                <Button variant="ghost" size="icon" onClick={() => {
                                    const parentView = currentSubView.split('-')[0] as AiMemoSubView;
                                    navigateTo(parentView);
                                }}>
                                    <ArrowLeft className="h-5 w-5" />
                                </Button>
                            )}
                            <div className="flex-1 min-w-0">
                                <h1 className="text-lg font-semibold truncate"><Trans>My Channel AI</Trans></h1>
                                <p className="text-xs text-muted-foreground">{getTitle()} <span className="text-meta-sm text-muted-foreground">beta</span></p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            {/* Mobile Sidebar Trigger */}
                            {isMobileOrTablet && (
                                <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                                    <SheetTrigger asChild>
                                        <Button variant="icon-box">
                                            <PanelRight className="w-5 h-5 text-primary" />
                                        </Button>
                                    </SheetTrigger>
                                    <SheetContent side="right" className="w-[280px] p-0">
                                        <AiMemoSidebarContent
                                            currentView={getSidebarView()}
                                            onChangeView={handleSidebarViewChange}
                                            onClose={() => setSheetOpen(false)}
                                        />
                                    </SheetContent>
                                </Sheet>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Content + Right Sidebar */}
            <div className="flex flex-1 overflow-hidden">
                {/* Main Content — 고정 높이 컨테이너, 내부 스크롤 */}
                <main className="flex-1 overflow-hidden bg-background">
                    <div className="h-full overflow-y-auto">
                        {currentSubView === 'memos' && (
                            <AiMemoList
                                roomId={roomIdParam}
                                onNewMemo={() => setShowMemoCreate(true)}
                                onOpenMemo={(id: string) => setDetailMemoId(id)}
                            />
                        )}
                        {currentSubView === 'minutes' && (
                            <AiMinutesList
                                roomId={roomIdParam}
                                onNewMinutes={() => setShowMinutesCreate(true)}
                                onOpenMinutes={(id: string) => setDetailMinutesId(id)}
                            />
                        )}
                    </div>
                </main>

                {/* Right Sidebar — Desktop only */}
                {!isMobileOrTablet && (
                    <aside className="w-64 flex flex-col shrink-0 border-l border-border bg-background">
                        <AiMemoSidebarContent
                            currentView={getSidebarView()}
                            onChangeView={handleSidebarViewChange}
                        />
                    </aside>
                )}
            </div>

            {/* ========== 메모 작성 모달 ========== */}
            <Dialog open={showMemoCreate} onOpenChange={() => { }}>
                <DialogContent hideTitle hideClose className="sm:max-w-2xl h-[90vh] max-h-[90vh] overflow-hidden p-0" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
                    <AiMemoCreate
                        roomId={roomIdParam}
                        onSaved={() => setShowMemoCreate(false)}
                        onCancel={() => setShowMemoCreate(false)}
                    />
                </DialogContent>
            </Dialog>

            {/* ========== 메모 디테일/편집 모달 ========== */}
            <Dialog open={!!detailMemoId} onOpenChange={(open) => { if (!open) setDetailMemoId(null); }}>
                <DialogContent hideTitle hideClose className="sm:max-w-2xl max-h-[90vh] overflow-hidden p-0">
                    {detailMemoId && (
                        <AiMemoDetail
                            roomId={roomIdParam}
                            memoId={detailMemoId}
                            onBack={() => setDetailMemoId(null)}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* ========== 회의록 작성 모달 ========== */}
            <Dialog open={showMinutesCreate} onOpenChange={() => { }}>
                <DialogContent hideTitle hideClose className="sm:max-w-2xl max-h-[90vh] overflow-hidden p-0" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
                    <AiMinutesCreate
                        roomId={roomIdParam}
                        onSaved={() => setShowMinutesCreate(false)}
                        onCancel={() => setShowMinutesCreate(false)}
                        onViewMinutes={(id) => { setShowMinutesCreate(false); setDetailMinutesId(id); }}
                    />
                </DialogContent>
            </Dialog>

            {/* ========== 회의록 상세 모달 ========== */}
            <Dialog open={!!detailMinutesId} onOpenChange={(open) => { if (!open) setDetailMinutesId(null); }}>
                <DialogContent hideTitle hideClose className="sm:max-w-2xl max-h-[90vh] overflow-hidden p-0">
                    {detailMinutesId && (
                        <AiMinutesDetail
                            roomId={roomIdParam}
                            minutesId={detailMinutesId}
                            onBack={() => setDetailMinutesId(null)}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AiMemoPage;
